import { useEffect, useState } from 'react';
import { RotateCcw, X, ZoomIn, ZoomOut } from 'lucide-react';

export const MAX_CHAT_ATTACHMENTS = 8;

export function ChatPhotoSelection({ photos = [], onRemove }) {
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    const next = photos.map(file => ({ file, url: URL.createObjectURL(file) }));
    setPreviews(next);
    return () => next.forEach(item => URL.revokeObjectURL(item.url));
  }, [photos]);

  if (!photos.length) return null;

  return (
    <div className="shrink-0 border-t border-white/10 px-4 py-2.5" data-testid="chat-photo-selection">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-xs font-semibold text-gray-300">
          {photos.length} photo{photos.length === 1 ? '' : 's'} ready
        </span>
        <span className="text-[11px] text-gray-500">{photos.length}/{MAX_CHAT_ATTACHMENTS}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {previews.map((item, index) => (
          <div key={`${item.file.name}-${item.file.lastModified}-${index}`} className="relative h-16 w-16 shrink-0">
            <img
              src={item.url}
              alt={item.file.name || `Selected photo ${index + 1}`}
              className="h-full w-full rounded-lg border border-white/10 object-cover"
            />
            <button
              type="button"
              onClick={() => onRemove?.(index)}
              className="absolute -right-1.5 -top-1.5 grid h-6 w-6 place-items-center rounded-full bg-gray-950 text-white shadow-lg"
              aria-label={`Remove ${item.file.name || `photo ${index + 1}`}`}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const clampZoom = value => Math.min(4, Math.max(1, value));

export function ChatPhotoViewer({ photo, onClose }) {
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    setZoom(1);
  }, [photo?.url]);

  useEffect(() => {
    if (!photo) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [photo, onClose]);

  if (!photo?.url) return null;

  const updateZoom = delta => setZoom(current => clampZoom(current + delta));

  return (
    <div
      className="fixed inset-0 z-[120] flex min-h-0 flex-col"
      style={{ background: 'rgba(3,8,18,0.94)', backdropFilter: 'blur(12px)' }}
      onClick={onClose}
      data-testid="chat-photo-viewer"
    >
      <div
        className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-3 pb-3"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
        onClick={event => event.stopPropagation()}
      >
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-white">
          {photo.filename || 'Chat photo'}
        </p>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={() => updateZoom(-0.5)}
            disabled={zoom <= 1}
            className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white disabled:opacity-35"
            aria-label="Zoom out"
          >
            <ZoomOut className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setZoom(1)}
            disabled={zoom === 1}
            className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white disabled:opacity-35"
            aria-label="Reset zoom"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => updateZoom(0.5)}
            disabled={zoom >= 4}
            className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white disabled:opacity-35"
            aria-label="Zoom in"
          >
            <ZoomIn className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full bg-white text-gray-950 shadow-lg"
            aria-label="Close photo viewer"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div
        className="min-h-0 flex-1 overflow-auto p-3"
        style={{ touchAction: 'pan-x pan-y' }}
        onClick={event => event.stopPropagation()}
      >
        <div className="flex min-h-full min-w-full items-center justify-center">
          <img
            src={photo.url}
            alt={photo.filename || 'Chat photo preview'}
            draggable="false"
            onDoubleClick={() => setZoom(current => current === 1 ? 2 : 1)}
            className="block rounded-lg object-contain transition-[width] duration-150"
            style={{
              width: zoom === 1 ? 'auto' : `${zoom * 100}%`,
              maxWidth: zoom === 1 ? '100%' : 'none',
              maxHeight: zoom === 1 ? 'calc(100dvh - 6.5rem)' : 'none',
            }}
          />
        </div>
      </div>
    </div>
  );
}
